import { HeartHandshake, Brain, Sparkles } from "lucide-react";
import { useMental } from "@/context/MentalContext";

const pillars = [
  {
    icon: Brain,
    title: "Stress & Emotional Balance",
    desc: "Understand your current stress load and emotional well-being with simple, respectful check-ins.",
  },
  {
    icon: HeartHandshake,
    title: "Compassionate & Safe",
    desc: "If you're going through a hard time, we pause the assessment and point you to real human support.",
  },
  {
    icon: Sparkles,
    title: "Personal, Not Prescriptive",
    desc: "Get gentle suggestions — breathing, sleep habits, mindful movement, Indian meals — never a diagnosis.",
  },
];

export default function MentalSection() {
  const { openMental } = useMental();

  return (
    <section
      id="mental"
      data-testid="mental-section"
      className="relative py-28 md:py-36 overflow-hidden"
    >
      <div className="absolute inset-0 grid-radial opacity-60 pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[500px] rounded-full bg-cyan-500/[0.06] blur-[140px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="text-xs font-semibold uppercase tracking-[0.25em] text-cyan-400 mb-5">
            Mental Wellness Check-in
          </div>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-[-0.03em] leading-tight">
            How you feel is{" "}
            <span className="text-gradient-cyan">health too.</span>
          </h2>
          <p className="mt-6 text-zinc-400 text-lg leading-relaxed">
            A 2-minute AI-powered wellness check-in — stress, sleep, mood,
            lifestyle. You'll receive an{" "}
            <span className="text-white font-medium">
              explainable Mental Wellness Score
            </span>{" "}
            with kind, personalized suggestions. Never a diagnosis.
          </p>

          <button
            data-testid="mental-start-btn"
            onClick={openMental}
            className="mt-9 inline-flex items-center gap-2 px-8 py-4 rounded-full btn-glow text-white font-medium animate-pulse-glow"
          >
            <Brain className="w-4 h-4" />
            Start Mental Wellness Check-in
          </button>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 mt-4">
          {pillars.map((p) => (
            <div
              key={p.title}
              className="glass-card rounded-3xl p-6 hover:-translate-y-1 hover:border-cyan-400/40 transition-all"
            >
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#0B3D91]/30 to-cyan-500/10 border border-cyan-500/20 flex items-center justify-center mb-4">
                <p.icon className="w-5 h-5 text-cyan-400" />
              </div>
              <h3 className="font-display text-lg font-semibold">{p.title}</h3>
              <p className="mt-2 text-sm text-zinc-400 leading-relaxed">
                {p.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
